import {
  ClockInput,
  addAgendaItem,
  addDocument,
  AmountField,
  ConfirmDialog,
  deleteAgendaItem,
  Dialog,
  getPlan,
  InvalidInput,
  Pick,
  removeDocument,
  updateAgendaItem,
  uploadDocumentFile,
  type AgendaInput,
  type AgendaItem,
  type Plan,
  type PlanStop,
  type Spot,
} from '@trippath/shared';
import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { stopDays } from './cityDays';
import { timeOf } from '../../lib/clock';
import { FileView, type Viewed } from '../../components/file/FileView';
import { dayShort, problemKey } from '../../lib/format';
import { SpotField } from '../../components/place/SpotField';
import { useTownMoney } from '../../lib/townMoney';
import { draftOf, wayOf, WayFields } from '../../components/way/WayFields';

interface Props {
  plan: Plan;
  stop: PlanStop;
  /** The thing changed; none for a new one. */
  item?: AgendaItem | undefined;
  /** The day a new one is done. */
  day: string;
  /** Where a new one is: a place found, its name the thing's; or a spot pressed on the map, by
   * its GPS, the thing still to be named. */
  at?: Spot | undefined;
  onClose: () => void;
  onSaved: (plan: Plan) => void;
}

/**
 * A thing done at a stop, added or changed: what it is, its day, when it starts and ends on the
 * local clock, where it is in the town, what it costs, whether it is booked, its link.
 */
export function AgendaDialog({ plan, stop, item, day, at, onClose, onSaved }: Props) {
  const { t, i18n } = useTranslation();
  const ids = useId();
  const first = useRef<HTMLInputElement>(null);
  const [name, setName] = useState(item?.name ?? (at?.detail === 'GPS' ? '' : at?.name) ?? '');
  const [on, setOn] = useState(item?.day ?? day);
  const [starts, setStarts] = useState(item?.starts ?? '');
  const [ends, setEnds] = useState(item?.ends ?? '');
  const [place, setPlace] = useState<Spot | null>(
    item?.place ? { ...item.place, detail: '' } : (at ?? null),
  );
  // Priced in its own money, as its ticket is: the town's, unless it was said in another.
  const kept = item?.paid ?? item?.price ?? null;
  const [price, setPrice] = useState<number | null>(kept?.minor ?? null);
  // Picked, or as it was kept; else the town's when there is a rate for it, else the trip's.
  const [picked, setPicked] = useState<string | null>(kept?.currency ?? null);
  const town = useTownMoney(plan.trip.id, stop.currency, plan.trip.currency, on);
  const currency = picked ?? town;
  // The moneys to pick from: the one it is in, the trip's own, then those of the trip's stops.
  const moneys = [
    ...new Set([
      currency,
      plan.trip.currency,
      ...plan.stops.flatMap((one) => (one.currency === null ? [] : [one.currency])),
    ]),
  ];
  const [booked, setBooked] = useState(item?.booked ?? false);
  // Its photos and PDFs: those kept, less any taken away, and those added, kept once it is.
  const [filesKept, setFilesKept] = useState(item?.files ?? []);
  const [adding, setAdding] = useState<File[]>([]);
  const [looking, setLooking] = useState<Viewed | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const [link, setLink] = useState(item?.link ?? '');
  // How one gets there from the place before: walking unless said, and how long.
  const [way, setWay] = useState(() => draftOf(item));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [failed, setFailed] = useState(false);
  const [removing, setRemoving] = useState(false);

  const submit = (event: { preventDefault: () => void }) => {
    event.preventDefault();
    const from = timeOf(starts);
    const until = timeOf(ends);
    const said = wayOf(way);
    const missing: Record<string, string> = {};
    if (name.trim() === '') missing.name = t('city.thing.required');
    if (from === undefined) missing.starts = t('city.thing.wrongTime');
    if (until === undefined) missing.ends = t('city.thing.wrongTime');
    if (typeof said === 'string') missing.way = t(`city.thing.${said}`);
    setErrors(missing);
    if (from === undefined || until === undefined || name.trim() === '') return;
    if (typeof said === 'string') return;
    const input: AgendaInput = {
      stop: stop.id,
      name: name.trim(),
      day: on,
      starts: from,
      ends: until,
      place: place ? { lat: place.lat, lon: place.lon, name: place.name } : null,
      price,
      currency,
      booked,
      link: link.trim() === '' ? null : link.trim(),
      ...said,
    };
    setFailed(false);
    // The files added go with it once it is kept: a new one by the id it is given.
    const before = new Set(plan.stops.flatMap((one) => one.agenda.map((thing) => thing.id)));
    const attach = (next: Plan) => {
      const id =
        item?.id ??
        next.stops.flatMap((one) => one.agenda).find((thing) => !before.has(thing.id))?.id;
      if (adding.length === 0 || id === undefined) return next;
      return Promise.all(
        adding.map((file) =>
          addDocument(plan.trip.id, {
            kind: 'other',
            name: file.name,
            note: '',
            validFrom: null,
            validTo: null,
            stop: stop.id,
            private: false,
            traveller: null,
            activity: id,
          }).then((document) => uploadDocumentFile(plan.trip.id, document.id, file)),
        ),
      ).then(() => getPlan(plan.trip.id));
    };
    (item ? updateAgendaItem(plan.trip.id, item.id, input) : addAgendaItem(plan.trip.id, input))
      .then(attach)
      .then(onSaved, (error: unknown) => {
        // No rate for the money it is in: said at the price.
        if (error instanceof InvalidInput) {
          setErrors(
            Object.fromEntries(
              error.fields.map((f) => [
                f.field === 'currency' ? 'price' : f.field,
                t(problemKey(f.problem)),
              ]),
            ),
          );
        } else {
          setFailed(true);
        }
      });
  };

  const time = (
    id: string,
    label: string,
    value: string,
    set: (v: string) => void,
    hint: string,
  ) => (
    <div className="field">
      <label className="field-label" htmlFor={`${ids}-${id}`}>
        {label}
      </label>
      <ClockInput
        id={`${ids}-${id}`}
        className="text-input booking-time"
        placeholder={hint}
        value={value}
        aria-invalid={errors[id] !== undefined}
        onChange={set}
      />
      {errors[id] !== undefined && <p className="field-error">{errors[id]}</p>}
    </div>
  );

  return (
    <>
      <Dialog
        open
        onClose={onClose}
        title={
          item
            ? t('city.thing.editTitle', { name: item.name })
            : t('city.thing.newTitle', { city: stop.city })
        }
        initialFocus={first}
      >
        <form className="form" onSubmit={submit} noValidate>
          <div className="field">
            <label className="field-label" htmlFor={`${ids}-name`} data-required>
              {t('city.thing.name')}
            </label>
            <input
              id={`${ids}-name`}
              ref={first}
              className="text-input"
              type="text"
              placeholder={t('city.thing.nameHint')}
              value={name}
              aria-invalid={errors.name !== undefined}
              onChange={(event) => {
                setName(event.target.value);
              }}
            />
            {errors.name !== undefined && <p className="field-error">{errors.name}</p>}
          </div>
          {/* When: the day, and its start and end beside it on one line. */}
          <div className="agenda-when">
            <Pick
              label={t('city.thing.day')}
              options={stopDays(stop).map((one) => ({
                value: one,
                label: dayShort(one, i18n.language),
              }))}
              value={on}
              searchable={false}
              onChange={setOn}
            />
            {time('starts', t('city.thing.starts'), starts, setStarts, '10:00')}
            {time('ends', t('city.thing.ends'), ends, setEnds, '12:00')}
          </div>
          <WayFields
            way={way}
            onWay={setWay}
            near={stop.place}
            end={name.trim() === '' ? t('city.thing.here') : name.trim()}
            error={errors.way}
          />
          <SpotField
            label={t('city.thing.place')}
            near={stop.place}
            spot={place}
            onPick={setPlace}
            onClear={() => {
              setPlace(null);
            }}
          />
          <div className="form-row">
            <AmountField
              key={currency}
              label={t('city.thing.price')}
              value={price}
              onChange={setPrice}
              currency={currency}
              currencies={moneys}
              onCurrency={(code) => {
                setPicked(code);
                setPrice(null);
              }}
              error={errors.price}
            />
            <div className="field">
              <label className="field-label" htmlFor={`${ids}-link`}>
                {t('city.thing.link')}
              </label>
              <input
                id={`${ids}-link`}
                className="text-input"
                type="url"
                placeholder="https://"
                value={link}
                onChange={(event) => {
                  setLink(event.target.value);
                }}
              />
            </div>
          </div>
          <label className="check">
            <input
              type="checkbox"
              checked={booked}
              onChange={(event) => {
                setBooked(event.target.checked);
              }}
            />
            {t('city.thing.booked')}
          </label>
          {/* What one looks at on the day: the house, a guide's map, the ticket's QR code. */}
          <fieldset className="thing-files">
            <legend className="field-label">{t('city.thing.files')}</legend>
            <ul>
              {filesKept.map((file) => (
                <li key={file.id}>
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => {
                      setLooking(file);
                    }}
                  >
                    {file.name}
                  </button>
                  <button
                    type="button"
                    className="icon-button icon-button-small"
                    aria-label={t('city.thing.removeFile', { name: file.name })}
                    onClick={() => {
                      void removeDocument(plan.trip.id, file.id).then(() => {
                        setFilesKept((was) => was.filter((one) => one.id !== file.id));
                      });
                    }}
                  >
                    ×
                  </button>
                </li>
              ))}
              {adding.map((file, at) => (
                <li key={`${file.name}-${String(at)}`}>
                  <span>{file.name}</span>
                  <span className="quiet">{t('city.thing.fileOnSave')}</span>
                  <button
                    type="button"
                    className="icon-button icon-button-small"
                    aria-label={t('city.thing.removeFile', { name: file.name })}
                    onClick={() => {
                      setAdding((was) => was.filter((_, i) => i !== at));
                    }}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
            <input
              ref={fileInput}
              type="file"
              accept="image/*,application/pdf"
              multiple
              hidden
              tabIndex={-1}
              aria-hidden="true"
              data-testid="thing-files"
              onChange={(event) => {
                const chosen = [...(event.target.files ?? [])];
                setAdding((was) => [...was, ...chosen]);
                event.target.value = '';
              }}
            />
            <button
              type="button"
              className="button button-small"
              onClick={() => {
                fileInput.current?.click();
              }}
            >
              {t('city.thing.addFile')}
            </button>
          </fieldset>
          {failed && (
            <p className="form-failed" role="alert">
              {t('plan.failed')}
            </p>
          )}
          <div className="dialog-actions">
            {item && (
              <button
                type="button"
                className="button button-quiet dialog-action-apart"
                onClick={() => {
                  setRemoving(true);
                }}
              >
                {t('city.thing.remove')}
              </button>
            )}
            <button type="button" className="button" onClick={onClose}>
              {t('dialog.cancel')}
            </button>
            <button type="submit" className="button button-primary">
              {item ? t('city.thing.save') : t('city.thing.add')}
            </button>
          </div>
        </form>
      </Dialog>
      {item && (
        <ConfirmDialog
          open={removing}
          title={t('city.thing.removeQuestion', { name: item.name })}
          body={t('city.thing.removeBody')}
          confirmLabel={t('city.thing.removeConfirm')}
          onCancel={() => {
            setRemoving(false);
          }}
          onConfirm={() => {
            setRemoving(false);
            deleteAgendaItem(plan.trip.id, item.id).then(onSaved, () => {
              setFailed(true);
            });
          }}
        />
      )}
      {looking && (
        <FileView
          trip={plan.trip.id}
          file={looking}
          onClose={() => {
            setLooking(null);
          }}
        />
      )}
    </>
  );
}
